<script context="module">
  import Embed from "@layouts/Embed.svelte"

  export const layout = Embed
</script>

<script>
  import { onDestroy, onMount, tick } from "svelte"
  import { page } from "@inertiajs/svelte"
  import LoadNextPage from "@objects/LoadNextPage.svelte"
  import IntersectionObserver from "@objects/IntersectionObserver.svelte"
  import Button from "@components/Button.svelte"
  import EmbeddedProductCard from "@components/EmbeddedProductCard.svelte"
  import EmbedHeader from "./EmbedHeader.svelte"

  export let collection
  export let products

  const account = $page.props.account

  const request = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, property) => searchParams.get(property) === "1",
  })

  const nextOpts = {
    onComplete: () => {
      sendParentResizeMessage()

      setTimeout(() => {
        window.affirm?.ui?.refresh?.()
      }, 0)
    },
  }

  let container
  let cachedContainerHeight
  let intervalId

  async function sendParentResizeMessage() {
    if (!request.autosize) {
      return
    }

    await tick()

    if (cachedContainerHeight === container.offsetHeight) {
      return
    }
    cachedContainerHeight = container.offsetHeight

    window.parent.postMessage(
      {
        embedId: `onsidedoor-embed-${collection.id}`,
        command: "resize",
        height: cachedContainerHeight,
      },
      "*",
    )
  }

  onMount(() => {
    sendParentResizeMessage()

    // The section containing the pagination can change heights after the initial
    // calculation, for example if the user scrolls too fast the IntersectionObserver
    // will miss an intersection a button is shown which changes the height of the container.
    // This hack will account for any height issues to ensure that no content is cut off
    // at the bottom of the embed.
    intervalId = setInterval(() => {
      sendParentResizeMessage()
    }, 250)
  })

  onDestroy(() => {
    clearInterval(intervalId)
  })
</script>

<svelte:window on:resize={sendParentResizeMessage} />

<section
  bind:this={container}
  class:rounded={request.border}
  class:border={request.border}
  class:pb-8={request.padding}
>
  {#if request.header}
    <div class:p-6={request.header} class:border-b={request.header} class="[ border-neutral-200 ]">
      <EmbedHeader {collection} {account} />
    </div>
  {/if}

  <div class:p-6={request.padding}>
    <section
      class="
        [ grid grid-cols-2 gap-3 ]
        [ sm:grid-cols-3 sm:gap-4 ]
        [ md:grid-cols-4 ]
        [ lg:gap-6 lg:grid-cols-5 ]
      "
    >
      {#each products.data as product}
        <EmbeddedProductCard {product} {collection} shouldUsePostMessageNavigation={request.externalNavigation} />
      {/each}
    </section>

    <section class="pt-24">
      <LoadNextPage bind:items={products} let:isLoading let:onLastPage let:next>
        <IntersectionObserver on:intersect={next(nextOpts)} bottom="200%">
          <div class="mt-12 flex justify-center">
            {#if onLastPage}
              <p class="text-sm text-center italic">All your products have been loaded</p>
            {:else if isLoading}
              <p class="text-sm text-center italic">Loading more products...</p>
            {:else}
              <Button on:click={next(nextOpts)} type="neutral">Load more products</Button>
            {/if}
          </div>
        </IntersectionObserver>
      </LoadNextPage>
    </section>
  </div>
</section>
